import { combineLatest, Observable, Subject } from 'rxjs'
import { Injectable } from '@angular/core'
import { map, startWith, shareReplay } from 'rxjs/operators'
import { CookieService } from './cookie.service'
import { VerticalService } from './vertical.service'
import { SegmentService } from '../shared/analytics/services/segment.service'
import { AuthService } from './auth/auth.service'
import { DEVICES, UserAgentService } from './user-agent.service'
import { ExperimentationService } from '../shared/experimentation/experimentation.service'

// TODO - remove or rename. This is still used in app-interstitial.component, and nav-pill.component

export interface IMobileSmartBannerConfig {
  siteId?: number | undefined
  showMobileBanner?: boolean
  mobileBannerText?: IMobileSmartBannerText
  bannerLink?: string
  analyticsName?: string
  isAndroid?: boolean
}

export interface IMobileSmartBannerText {
  header: string
  subheader: string
  buttonPrompt: string
}

export interface IMobileSmartBannerNodeProps {
  node_id?: number
  node_type?: string
  web_site_id?: number
  web_url?: string
}

export interface IMobileSmartBannerService {
  smartBannerConfig$: Observable<IMobileSmartBannerConfig>
  updateNodeProps(props: IMobileSmartBannerNodeProps | undefined): void
  handleBannerDismissal(): void
}

export const mobileSmartBannerCookie = 'hasDismissedMobileAppBanner'

@Injectable({
  providedIn: 'root'
})
export class MobileSmartBannerService implements IMobileSmartBannerService {
  private BANNER_BASE_LINK = 'https://flosports.app.link/'
  private nodePropsSource = new Subject<IMobileSmartBannerNodeProps | undefined>()
  private nodeProps$ = this.nodePropsSource.asObservable()

  private mobileSmartBanner$ = this.authService.isPremium$.pipe(
    map(isPremium => {
      const isAndroid = this.userAgentService.userAgent.device === DEVICES.ANDROID
      return {
        canViewBanner: isPremium && this.userAgentService.deviceIsAndroidOrSupportedIPhone,
        mobileBannerText: {
          header: `FloSports for ${isAndroid ? 'Android' : 'iOS'}`,
          subheader: 'Watch live events, replays and more',
          buttonPrompt: 'View in the app'
        },
        isAndroid
      }
    })
  )

  public smartBannerConfig$ = combineLatest([
    this.cookieService.cookies$,
    this.nodeProps$.pipe(startWith(undefined)),
    this.vs.siteId$,
    this.es.activeExperiments,
    this.mobileSmartBanner$
  ]).pipe(
    map(([cookies, nodeProps, siteId, activeExperiments, mobileSmartBanner]) => {
      const hasDismissedBanner = this.cookieService.get(mobileSmartBannerCookie)

      const webAnonymousId = this.segment.anonymousId || ''
      const linkParams = {
        ...nodeProps,
        web_site_id: siteId,
        web_anonymous_id: webAnonymousId,
        web_active_experiments: activeExperiments,
        jwtToken: this.authService.getTokenFromStore(),
        refreshToken: this.authService.getRefreshTokenFromStore()
      }
      const bannerLink = buildBannerLink(this.BANNER_BASE_LINK, linkParams)

      return {
        siteId,
        bannerLink,
        isAndroid: mobileSmartBanner.isAndroid,
        showMobileBanner: mobileSmartBanner.canViewBanner && !hasDismissedBanner,
        showMobileNavPill: !!mobileSmartBanner.canViewBanner,
        analyticsName: 'View In Mobile App',
        mobileBannerText: mobileSmartBanner.mobileBannerText
      }
    }),
    shareReplay(1)
  )

  public updateNodeProps(props: IMobileSmartBannerNodeProps | undefined) {
    this.nodePropsSource.next(props)
  }

  public handleBannerDismissal() {
    this.cookieService.set(mobileSmartBannerCookie, true, {
      expires: 30
    })
  }

  constructor(
    private es: ExperimentationService,
    private userAgentService: UserAgentService,
    private vs: VerticalService,
    private cookieService: CookieService,
    private segment: SegmentService,
    private authService: AuthService
  ) {}
}

function buildBannerLink(baseLink: string, queryParams: any): string {
  const queryString = Object.keys(queryParams)
    .map(key => `${key}=${queryParams[key]}`)
    .join('&')
  return !!queryString ? `${baseLink}?${queryString}` : baseLink
}
